import React, { useRef, useState } from 'react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { NodeViewWrapper, ReactNodeViewProps } from '@tiptap/react';

/**
 * Pasted video link → embedded player (spec 046 FR-003, research Decision 10).
 * YouTube privacy-enhanced iframe supplies play/pause/volume natively; this frame adds
 * stop (reload = pause + reset to start) and expand-to-full / reduce-view controls.
 */
const VideoEmbedNode: React.FC<ReactNodeViewProps> = ({ node }) => {
  const { videoId, originalUrl } = node.attrs as { videoId: string; originalUrl: string };
  const [expanded, setExpanded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const frameRef = useRef<HTMLIFrameElement>(null);

  if (!videoId) {
    return (
      <NodeViewWrapper as="div">
        <a href={originalUrl} target="_blank" rel="noopener noreferrer">{originalUrl}</a>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="div">
      <Box
        sx={{
          my: 1.5,
          mx: expanded ? 0 : 'auto',
          maxWidth: expanded ? '100%' : 560,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
        data-testid="gn-video-embed"
      >
        <Box sx={{ position: 'relative', pb: '56.25%', height: 0 }}>
          <iframe
            key={reloadKey}
            ref={frameRef}
            src={`https://www.youtube-nocookie.com/embed/${videoId}`}
            title="Embedded video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
          />
        </Box>
        <Stack direction="row" justifyContent="flex-end" sx={{ bgcolor: 'action.hover', px: 0.5 }}>
          <Tooltip title="Stop">
            <IconButton size="small" onClick={() => setReloadKey(k => k + 1)}>
              <StopIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={expanded ? 'Reduce view' : 'Expand to full view'}>
            <IconButton size="small" onClick={() => setExpanded(e => !e)}>
              {expanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </NodeViewWrapper>
  );
};

export default VideoEmbedNode;
